import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Heart, Mail, BookOpen, Users, Sparkles, Lock } from "lucide-react";
import { api } from "@/lib/api";
import InitialsAvatar from "@/components/InitialsAvatar";

const HERO_IMG = "https://static.prod-images.emergentagent.com/jobs/501ff75c-3ac8-43b3-9bce-7aac7ef89b3b/images/f8ca451295e0a6a806af8b9941d8f64ce52fca18ebac6769a56a7e9ee691f43b.png";

export default function Landing() {
  const [featuredCounselors, setFeaturedCounselors] = useState([]);
  useEffect(() => {
    api
      .get("/therapists")
      .then(({ data }) => setFeaturedCounselors((data || []).slice(0, 4)))
      .catch(() => {});
  }, []);
  return (
    <div data-testid="page-landing">
      {/* Hero */}
      <section className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-20 lg:pt-28 pb-24 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7 animate-fade-up">
          <div className="text-xs tracking-[0.25em] uppercase text-mindora-red mb-6">
            <span className="editorial-rule" />A place to be honest
          </div>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-mindora-ink leading-[1.05] tracking-tight">
            The thing you can't<br />
            say out loud<br />
            <span className="italic text-mindora-red">someone else already has.</span>
          </h1>
          <p className="mt-8 text-lg leading-relaxed text-mindora-muted max-w-xl">
            Mindora is a story-sharing space for young people carrying things quietly —
            grades, friendships, family, grief, the everyday loneliness. Write under your name
            or anonymously. Press <em className="text-mindora-ink">I Relate</em> instead of typing
            a comment. Discover that you have never been the only one.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <Link
              to="/register"
              data-testid="landing-cta-begin"
              className="btn-primary rounded-full px-7 py-3 text-sm font-medium tracking-wide"
            >
              Begin your story
            </Link>
            <Link
              to="/feed"
              data-testid="landing-cta-read"
              className="rounded-full px-7 py-3 text-sm font-medium tracking-wide border border-mindora-line text-mindora-ink hover:bg-mindora-panel transition-colors"
            >
              Read others first
            </Link>
          </div>
          <div className="mt-10 flex items-center gap-3 text-sm text-mindora-muted">
            <Lock size={14} className="text-mindora-red" />
            Anonymous if you want. Always your choice.
          </div>
        </div>

        <div className="lg:col-span-5 relative">
          <div className="relative rounded-3xl overflow-hidden border border-mindora-line shadow-clay">
            <img src={HERO_IMG} alt="" className="w-full h-[520px] object-cover" />
          </div>
          <div className="absolute -bottom-6 -left-6 w-56 rounded-2xl bg-mindora-bg border border-mindora-line shadow-clay p-5">
            <div className="flex items-center gap-2 text-mindora-red">
              <Heart size={16} fill="#A73C35" />
              <span className="font-serif text-2xl text-mindora-ink">2,847</span>
            </div>
            <p className="text-xs text-mindora-muted mt-1 leading-relaxed">
              people pressed <em>I Relate</em> on a story last week.
            </p>
          </div>
        </div>
      </section>

      {/* The four pillars */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          <FeatureCard
            className="md:col-span-7"
            icon={<BookOpen size={20} />}
            tag="Stories"
            title="Write what you can't say out loud."
            body="Tag it. Choose your name or anonymity. Hit publish. Someone, somewhere, will read it and feel less alone — and so will you."
            cta={{ to: "/feed", label: "Read the feed" }}
            tone="dark"
          />
          <FeatureCard
            className="md:col-span-5"
            icon={<Mail size={20} />}
            tag="Future Self"
            title="Write a letter you'll need later."
            body="Set a delivery date. We'll send it to your inbox when the moment comes."
            cta={{ to: "/future-self", label: "Begin a letter" }}
          />
          <FeatureCard
            className="md:col-span-5"
            icon={<Sparkles size={20} />}
            tag="Gratitude Jar"
            title="Small entries. Quiet shifts."
            body="A warm meal. A funny conversation. The walk you nearly skipped. Over weeks, a different perspective settles in."
            cta={{ to: "/gratitude", label: "Add today's note" }}
          />
          <FeatureCard
            className="md:col-span-7"
            icon={<Users size={20} />}
            tag="Empathy Toolkit"
            title="Practical scripts for difficult conversations."
            body="Perspective-taking. Real listening. Words that work when 'how are you' isn't enough. Try one before your next hard conversation."
            cta={{ to: "/empathy", label: "Open the toolkit" }}
            tone="dark"
          />
        </div>
      </section>

      {/* I Relate explainer */}
      <section className="bg-mindora-panel border-y border-mindora-line py-24">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 text-center">
          <div className="text-xs tracking-[0.25em] uppercase text-mindora-red mb-6">
            <span className="editorial-rule" />the i relate button
          </div>
          <h2 className="font-serif text-4xl sm:text-5xl text-mindora-ink leading-tight">
            Sometimes the bravest thing to say is just <em className="italic">"I've been there."</em>
          </h2>
          <p className="mt-6 text-lg text-mindora-muted leading-relaxed">
            No comments. No replies. No performance. One quiet button that says:
            I read your story, and I have lived something like it.
            When a story collects a hundred of these, it stops being a post.
            It becomes proof.
          </p>
        </div>
      </section>

      {/* Counselors */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-5">
          <div className="text-xs tracking-[0.25em] uppercase text-mindora-red mb-6">
            <span className="editorial-rule" />never alone in the deep end
          </div>
          <h2 className="font-serif text-4xl sm:text-5xl text-mindora-ink leading-tight">
            Real psychologists.<br />Real conversations.<br />
            <span className="italic text-mindora-red">When you're ready.</span>
          </h2>
          <p className="mt-6 text-lg text-mindora-muted leading-relaxed">
            Vetted counselors and clinical psychologists you can book directly inside Mindora.
            One-on-one sessions or small group workshops. Stories give you the courage —
            professionals help you carry it forward.
          </p>
          <Link
            to="/directory"
            data-testid="landing-cta-directory"
            className="btn-primary inline-block mt-8 rounded-full px-7 py-3 text-sm font-medium"
          >
            Meet the counselors
          </Link>
        </div>
        <div className="lg:col-span-7">
          <div className="grid grid-cols-2 gap-6" data-testid="landing-counselors-grid">
            {featuredCounselors.map((t) => (
              <Link
                key={t.id}
                to={`/directory/${t.id}`}
                data-testid={`landing-counselor-${t.id}`}
                className="rounded-2xl bg-mindora-panel border border-mindora-line overflow-hidden hover:border-mindora-red hover:shadow-clay transition-all"
              >
                <div className="h-44">
                  {t.photo_url ? (
                    <img src={t.photo_url} alt={t.name} className="w-full h-full object-cover" />
                  ) : (
                    <InitialsAvatar name={t.name} className="w-full h-full" />
                  )}
                </div>
                <div className="p-4">
                  <div className="font-serif text-xl text-mindora-ink leading-tight">{t.name}</div>
                  <div className="text-xs text-mindora-muted mt-1 tracking-wide">
                    {(t.specialties || []).slice(0, 2).join(" · ") || t.title}
                  </div>
                  <div className="text-[11px] text-mindora-red mt-2 tracking-wide">
                    {t.session_price_display}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-4xl mx-auto px-6 lg:px-10 pb-32 text-center">
        <h3 className="font-serif text-4xl sm:text-5xl text-mindora-ink leading-tight">
          Your story is allowed to be heavy.<br />
          <em className="italic text-mindora-red">It's also allowed to be heard.</em>
        </h3>
        <Link
          to="/register"
          data-testid="landing-cta-bottom"
          className="btn-primary inline-block mt-8 rounded-full px-9 py-4 text-base font-medium"
        >
          Create your space
        </Link>
      </section>
    </div>
  );
}

function FeatureCard({ icon, tag, title, body, cta, tone, className = "" }) {
  const dark = tone === "dark";
  return (
    <div
      className={`group rounded-3xl p-8 lg:p-10 border transition-all duration-500 hover:-translate-y-1 ${
        dark
          ? "bg-mindora-ink text-mindora-bg border-mindora-ink shadow-clay hover:shadow-clayHover"
          : "bg-mindora-panel border-mindora-line shadow-clay hover:shadow-clayHover"
      } ${className}`}
    >
      <div className={`flex items-center gap-3 text-xs uppercase tracking-[0.22em] ${dark ? "text-mindora-clay" : "text-mindora-red"}`}>
        <span className={`flex items-center justify-center w-8 h-8 rounded-full ${dark ? "bg-mindora-red/30" : "bg-mindora-red/10"}`}>
          {icon}
        </span>
        {tag}
      </div>
      <h3 className={`font-serif text-3xl lg:text-4xl leading-tight mt-6 ${dark ? "text-mindora-bg" : "text-mindora-ink"}`}>
        {title}
      </h3>
      <p className={`mt-4 text-base leading-relaxed ${dark ? "text-mindora-bg/80" : "text-mindora-muted"}`}>
        {body}
      </p>
      <Link
        to={cta.to}
        data-testid={`landing-feature-${tag.toLowerCase().replace(/\s+/g, "-")}`}
        className={`inline-block mt-7 text-sm tracking-wide border-b pb-0.5 transition-colors ${
          dark ? "text-mindora-bg border-mindora-clay hover:border-mindora-bg" : "text-mindora-red border-mindora-red hover:border-mindora-ink hover:text-mindora-ink"
        }`}
      >
        {cta.label} →
      </Link>
    </div>
  );
}
